import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { poppinsBoldBase64, poppinsRegularBase64 } from "./Font";

const headStyles = {
  fillColor: "#d3d3d3",
  textColor: "#000000",
  lineColor: [0, 0, 0],
  lineWidth: 0.1,
  fontStyle: "bold",
};

function safeText(v) {
  return v === null || v === undefined ? "" : String(v);
}

function formatDate(value) {
  if (!value) return "";

  const d = new Date(value);

  if (!isNaN(d.getTime())) {
    const pad = (n) => (n < 10 ? `0${n}` : n);

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  return String(value);
}

export const generateAuditPdf = (auditRows = [], headerInfo = {}, auditType = "Payroll") => {
  const doc = new jsPDF("l", "mm", "a2");

  /* =========================
     REGISTER FONTS
  ========================= */

  doc.addFileToVFS("Poppins-Regular.ttf", poppinsRegularBase64);
  doc.addFont("Poppins-Regular.ttf", "Poppins", "normal");

  doc.addFileToVFS("Poppins-Bold.ttf", poppinsBoldBase64);
  doc.addFont("Poppins-Bold.ttf", "Poppins", "bold");

  /* =========================
     LAYOUT
  ========================= */

  const PAGE_MARGIN = 12;
  const pageWidth = doc.internal.pageSize.getWidth();

  /* =========================
     COLUMN LOGIC
  ========================= */

  const isWages = headerInfo?.itemType === "Wages";
  const isEmployerTaxes = headerInfo?.itemType === "EmployerTaxes";

  const headers = auditType === "Employee"
      ? [
          "Employee Name",
          "Phone",
          "Email",
          "SSN",
          "Check Printed As",
          "Pay Frequency",
          "Department",
          "Job Title",
          "Action",
          "Action Date",
          "Action By",
          "Status",
        ]
      : [
          "Item Type",
          "Item Name",
          ...(isWages ? ["Pay Type", "Wage Type"] : []),
          "Department",
          "Job Title",
          "Expense COA",
          ...(isEmployerTaxes ? ["Payable COA"] : []),
          "Action",
          "Action Date",
          "Action By",
          "Status",
        ];

  /* =========================
     DATA
  ========================= */

  const rows = [];

  auditRows.forEach((audit) => {
    if (auditType === "Employee") {
        const details = Array.isArray(audit?.PayrollDetails)
            ? audit.PayrollDetails.filter(p => p?.Department || p?.JobTitle)
            : [];

        const uniquePayroll = [...new Map(details.map(item => [`${item.Department}-${item.JobTitle}`, item])).values()];

        if (uniquePayroll.length === 0) {
            rows.push([
                safeText(audit.EmployeeName),
                safeText(audit.MobileNum),
                safeText(audit.EmailID),
                safeText(audit.Ssn),
                safeText(audit.CheckToBePrinted),
                "", "", "",
                safeText(audit.ActionName),
                formatDate(audit.ActionDate),
                safeText(audit.ActionByName),
                safeText(audit.IsActive),
            ]);
        } else {
            uniquePayroll.forEach((p, index) => {
                rows.push([
                    safeText(audit.EmployeeName),
                    safeText(audit.MobileNum),
                    safeText(audit.EmailID),
                    safeText(audit.Ssn),
                    safeText(audit.CheckToBePrinted),
                    safeText(p.Frequency),
                    safeText(p.Department),
                    safeText(p.JobTitle),
                    safeText(audit.ActionName),
                    formatDate(audit.ActionDate),
                    safeText(audit.ActionByName),
                    safeText(audit.IsActive),
                ]);
            });
        }
    } else {
        const details = audit?.PayrollAuditDeptJobDetails || [];

        if (details.length === 0) {
          rows.push([
            safeText(audit.ItemType || headerInfo.itemType),
            safeText(audit.ItemName || headerInfo.ItemName),
            ...(isWages ? [safeText(audit.PayType), safeText(audit.WageType)] : []),
            "",
            "",
            "",
            ...(isEmployerTaxes ? [""] : []),
            safeText(audit.ActionName),
            formatDate(audit.ActionDate),
            safeText(audit.ActionByName),
            safeText(audit.IsActive),
          ]);
        } else {
          details.forEach((d, index) => {
            rows.push([
              safeText(audit.ItemType || headerInfo.itemType),
              safeText(audit.ItemName || headerInfo.ItemName),
              ...(isWages
                ? [safeText(audit.PayType), safeText(audit.WageType)]
                : []),
              safeText(d.Department),
              safeText(d.JobTitle),
              safeText(d.AccountName),
              ...(isEmployerTaxes ? [safeText(d.LiabilityAccName || "")] : []),
              safeText(audit.ActionName),
              formatDate(audit.ActionDate),
              safeText(audit.ActionByName),
              safeText(audit.IsActive),
            ]);
          });
        }
    }
  });

  /* =========================
     AUTO WIDTH
  ========================= */

  const totalColumns = headers.length;
  const colWidth =
    totalColumns > 0
      ? (pageWidth - PAGE_MARGIN * 2) / totalColumns
      : "auto";

  const columnStyles = {};

  headers.forEach((_, i) => {
    columnStyles[i] = { cellWidth: colWidth };
  });

  /* =========================
     TABLE
  ========================= */

  autoTable(doc, {
    startY: 30,
    head: [headers],
    body: rows,
    theme: "grid",

    didDrawPage: function (data) {
      // Header
      doc.setFont("Poppins", "bold");
      doc.setFontSize(12);
      doc.text(
        safeText(headerInfo.corpName || "Corporation"),
        pageWidth / 2,
        15,
        { align: "center" }
      );

      doc.setFont("Poppins", "normal");
      doc.setFontSize(10);
      doc.text(`${auditType} Audit Log`, pageWidth / 2, 22, {
        align: "center",
      });
    },

    headStyles: {
      ...headStyles,
      font: "Poppins",
      cellPadding: 2,
      valign: "middle",
    },

    styles: {
      font: "Poppins",
      fontSize: 8,
      overflow: "linebreak",
      cellPadding: 2,
    },

    bodyStyles: {
      minCellHeight: 6, // cell height for each row
    },

    columnStyles: columnStyles,

    margin: { top: 30, left: PAGE_MARGIN, right: PAGE_MARGIN },

    tableWidth: pageWidth - PAGE_MARGIN * 2,
  });

  doc.save(`${auditType}-Audit.pdf`);
};

 